import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import TemplatePinCard from '@/components/Tasks/TemplatePinCard';
import ToolCard from '@/components/Tools/ToolCard';
import { useModalStore } from '@/store';
import useRequireAuth from '@/hooks/useRequireAuth';
import { MAP_IDEAS } from '@/lib/mapsDiscovery';

const sections = {
  tasks: { title: 'Put a task on your schedule', subtitle: 'Choose a template, then review the schedule and cost before creating it.', route: '/tasks/browse', more: 'Browse tasks' },
  tools: { title: 'Find a tool for the job', subtitle: 'Open a tool to explore its inputs and see the cost before running it.', route: '/tools', more: 'Browse tools' },
  maps: { title: 'Discover somewhere new', subtitle: 'Explore coffee spots, walking trails, and a change of scenery in Maps.', route: '/maps', more: 'Open Maps' },
};

export default function DiscoveryFeatureSection({ kind, items = [], page = 0, palette, cardWidth }) {
  const router = useRouter();
  const { openModal } = useModalStore(s => s.actions);
  const requireAuth = useRequireAuth();
  const openSetup = template => requireAuth(() => openModal('SetupTaskFromTemplateModal', { template }), {
    source: 'discovery_task_setup', title: `Sign in to set up ${template.name}`,
    subtitle: 'Choose the inputs, schedule, and cost before creating your task.', icon: 'calendar-clock',
  });
  const section = sections[kind];
  const selection = kind === 'maps' ? (page === 0 ? MAP_IDEAS : []) : items.slice(page * 3, page * 3 + 3);
  if (!selection.length) return null;
  return <View style={styles.section} testID={`discovery-${kind}`}>
    <View style={styles.header}>
      <View style={{ flex: 1, gap: 5 }}><Text size="l" weight="semibold">{section.title}</Text><Text size="s" color="secondary">{section.subtitle}</Text></View>
      <Pressable accessibilityRole="link" accessibilityLabel={section.more} onPress={() => router.push(section.route)} style={styles.more}><Text size="s" color="secondary">{section.more}</Text><Icon name="arrow-right" size={12} color={palette.icon} /></Pressable>
    </View>
    <View style={styles.grid}>
      {selection.map(item => kind === 'tasks' ? <View key={item.slug} style={{ width: cardWidth, flexGrow: 1 }}><TemplatePinCard template={item} width="100%" stretchHeight discovery
        onPress={openSetup}
        onSchedulePress={openSetup} /></View>
        : kind === 'tools' ? <ToolCard key={item.slug} tool={item} measuredCardWidth={typeof cardWidth === 'number' ? cardWidth : undefined}
          onPress={() => router.push(`/tools/${encodeURIComponent(item.slug)}`)} style={{ width: cardWidth, flexGrow: 1 }} />
        : <Pressable key={item.query} accessibilityRole="link" accessibilityLabel={`Explore Maps: ${item.title}`}
          onPress={() => router.push({ pathname: '/maps', params: { query: item.query } })}
          style={({ pressed }) => [styles.mapCard, { width: cardWidth, backgroundColor: palette.surface, borderColor: palette.border, opacity: pressed ? 0.8 : 1 }]}>
          <Image source={{ uri: item.image }} contentFit="cover" style={styles.mapImage} />
          <View style={styles.mapBody}><Text size="xs" color="secondary">Maps</Text><Text size="l" weight="medium">{item.title}</Text><Text size="s" color="secondary">Choose an area to explore <Icon name="arrow-up-right" size={11} color={palette.icon} /></Text></View>
        </Pressable>)}
    </View>
  </View>;
}
const styles = StyleSheet.create({
  section: { width: '100%', gap: 16, paddingVertical: 20 },
  header: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 12 },
  more: { flexDirection: 'row', gap: 8, alignItems: 'center', minHeight: 36, paddingHorizontal: 4 },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 14 },
  mapCard: { borderWidth: 1, borderRadius: 20, overflow: 'hidden', flexGrow: 1 },
  mapImage: { width: '100%', height: 150 },
  mapBody: { padding: 20, gap: 9 },
});
